<template>
	<div ref="refChart" class="mob-kchart-container"></div>
</template>

<script setup>
import * as echarts from 'echarts/core';
import {
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent
} from 'echarts/components';
import { CandlestickChart, LineChart, BarChart } from 'echarts/charts';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import { ref, watch, nextTick, getCurrentInstance } from 'vue';
import { convertNumberToStr } from '@/utils/api';
import { createLegendOptions } from '@/utils/echarts-options';
import { convertPX } from '@/utils/echarts';
import { EN } from '@/locales';
import { useChartResize } from '../useChartResize';
import { useKChartCommon } from './useKChartCommon';

echarts.use([
	ToolboxComponent,
	TooltipComponent,
	GridComponent,
	VisualMapComponent,
	LegendComponent,
	BrushComponent,
	DataZoomComponent,
	CandlestickChart,
	LineChart,
	BarChart,
	CanvasRenderer,
	UniversalTransition
]);

const props = defineProps({
	data: {
		type: Array,
		default: () => []
	},
	excludeTable: {
		type: Boolean,
		default: false
	},
	fullCode: {
		type: String,
		default: ''
	}
});

const { proxy } = getCurrentInstance();

const {
	isHKStock,
	upColor,
	downColor,
	splitData,
	calculateMA,
	formatEnUnit,
	createTooltipPosition,
	createTooltipFormatter
} = useKChartCommon(props);

const maxData = ref(0);
let chartInstance = null;
const refChart = ref();

const { startListening, stopListening } = useChartResize(() => chartInstance);

function formatGrid() {
	return [
		{
			left: '2%',
			right: '2%',
			height: '55%',
			top: '3%',
			containLabel: true
		},
		{
			left: '2%',
			right: '2%',
			top: '66%',
			height: '25%',
			containLabel: true
		}
	];
}

const intMaxData = data => {
	let start = props.excludeTable ? data.values.length - 240 - 1 : data.values.length - 60 - 1;
	let end = data.values.length - 1;
	maxData.value = Math.max(...data.values.slice(start, end).map(item => item[6]));
};

const renderChart = () => {
	// 停止旧的监听
	stopListening();

	if (chartInstance) {
		chartInstance.dispose();
		chartInstance.clear();
		chartInstance = null;
	}

	if (!props.data || !props.data.length) return;

	// 确保数据按时间正序排列
	const arr = [...props.data].reverse();
	const data = splitData(arr);

	chartInstance = echarts.init(refChart.value);

	const option = {
		color: ['#fe9f26', '#3964e5'],
		animation: false,
		legend: {
			...createLegendOptions(),
			bottom: 10,
			left: 'center',
			data: [`${proxy.$t('enterprise.candle')}`, 'MA5', 'MA20']
		},
		tooltip: {
			trigger: 'axis',
			axisPointer: {
				type: 'line',
				axis: 'x'
			},
			triggerOn: 'click',
			borderWidth: 1,
			borderColor: '#ccc',
			padding: [convertPX(6), convertPX(10)],
			textStyle: {
				color: '#000'
			},
			position: createTooltipPosition(),
			formatter: createTooltipFormatter(data)
		},
		axisPointer: {
			link: [
				{
					xAxisIndex: 'all'
				}
			],
			label: {
				backgroundColor: '#777'
			}
		},
		visualMap: {
			show: false,
			seriesIndex: 3,
			dimension: 2,
			pieces: [
				{
					value: 1,
					color: downColor
				},
				{
					value: -1,
					color: upColor
				}
			]
		},
		grid: formatGrid(),
		xAxis: [
			{
				type: 'category',
				data: data.categoryData,
				boundaryGap: true,
				axisLine: {
					onZero: false,
					lineStyle: {
						color: '#E8EAEF'
					}
				},
				axisLabel: {
					show: false,
					color: '#989898',
					fontSize: convertPX(12)
				},
				splitLine: {
					show: true,
					lineStyle: {
						color: '#F3F3F3'
					}
				},
				min: 'dataMin',
				max: 'dataMax',
				axisPointer: {
					z: 100
				},
				axisTick: {
					show: false
				}
			},
			{
				type: 'category',
				gridIndex: 1,
				data: data.categoryData,
				boundaryGap: true,
				axisLine: {
					onZero: false,
					lineStyle: {
						color: '#E8EAEF'
					}
				},
				axisTick: { show: false },
				splitLine: {
					show: true,
					lineStyle: {
						color: '#F3F3F3'
					}
				},
				axisLabel: {
					show: true,
					color: '#4E5969'
				},
				min: 'dataMin',
				max: 'dataMax'
			}
		],
		yAxis: [
			{
				scale: true,
				splitLine: {
					lineStyle: { color: '#F2F3F5', type: 'solid' }
				},
				axisLabel: {
					show: true,
					color: '#989898',
					fontSize: convertPX(12),
					inside: true
				},
				axisLine: {
					show: true,
					lineStyle: {
						color: '#F2F3F5'
					}
				},
				splitNumber: 3,
				zlevel: 2
			},
			{
				scale: true,
				gridIndex: 1,
				splitNumber: 2,
				axisLabel: {
					show: true,
					color: '#989898',
					fontSize: convertPX(12),
					formatter(value, index) {
						if (index !== 0) {
							if (value < maxData.value) return; //移动端非最大值隐藏掉
							if (proxy.$i18n.locale === EN) return formatEnUnit(value);
							return convertNumberToStr(value) + `${isHKStock.value ? '股' : '手'}`;
						} else {
							return '';
						}
					},
					inside: true
				},
				axisLine: {
					show: true,
					lineStyle: {
						color: '#F2F3F5'
					}
				},
				axisTick: { show: false },
				splitLine: {
					show: true,
					lineStyle: { color: '#F2F3F5', type: 'solid' }
				},
				zlevel: 2
			}
		],
		dataZoom: [
			{
				type: 'inside',
				xAxisIndex: [0, 1],
				startValue: props.excludeTable ? data.values.length - 240 - 1 : data.values.length - 60 - 1,
				endValue: data.values.length - 1,
				disabled: props.excludeTable ? true : false
			},
			{
				show: false,
				xAxisIndex: [0, 1],
				type: 'slider',
				backgroundColor: 'rgba(57, 100, 229, 0.1)',
				dataBackground: {
					lineStyle: {
						width: 1,
						color: 'rgba(57, 100, 229, 0.2)'
					},
					areaStyle: {
						color: 'rgba(57, 100, 229, 0.1)'
					}
				},
				fillerColor: 'rgba(57, 100, 229, 0.3)',
				selectedDataBackground: {
					lineStyle: {
						width: 1,
						color: 'rgba(57, 100, 229, 0.4)'
					},
					areaStyle: {
						color: 'rgba(57, 100, 229, 0.3)'
					}
				},
				borderColor: 'rgba(57, 100, 229, 0.1)',
				borderRadius: 0,
				handleIcon: 'M1 0 H3 A1 1 0 0 1 4 1 V15 A1 1 0 0 1 3 16 H1 A1 1 0 0 1 0 15 V1 A1 1 0 0 1 1 0 Z',
				handleStyle: {
					color: 'rgba(57, 100, 229, 1)'
				},
				height: convertPX(24),
				bottom: 0,
				textStyle: {
					color: 'rgba(57, 100, 229, 1)',
					fontFamily: 'Barlow',
					fontSize: convertPX(10),
					lineHeight: convertPX(16)
				},
				brushSelect: false
			}
		],
		series: [
			{
				name: `${proxy.$t('enterprise.candle')}`,
				type: 'candlestick',
				data: data.values,
				barMaxWidth: convertPX(20),
				itemStyle: {
					color: 'transparent',
					color0: downColor,
					borderColor: upColor,
					borderColor0: undefined
				}
			},
			{
				name: 'MA5',
				type: 'line',
				data: calculateMA(5, data),
				symbol: 'none',
				smooth: true,
				lineStyle: {
					opacity: 0.5
				}
			},
			{
				name: 'MA20',
				type: 'line',
				data: calculateMA(20, data),
				symbol: 'none',
				smooth: true,
				lineStyle: {
					opacity: 0.5
				}
			},
			{
				name: 'Volume',
				type: 'bar',
				data: data.volumes,
				xAxisIndex: 1,
				yAxisIndex: 1,
				barMaxWidth: convertPX(20)
			}
		]
	};

	chartInstance.setOption(option, true);
	chartInstance.dispatchAction({
		type: 'brush'
	});

	// 设置交易量的y轴最大值
	intMaxData(data);
	chartInstance.setOption(option);

	// 启动响应式监听
	startListening();

	// 移动端特殊交互
	chartInstance.on('mousemove', function () {
		chartInstance.dispatchAction({
			type: 'hideTip'
		});
	});

	chartInstance.on('datazoom', params => {
		// 需要在dataZoom范围内找出最大的交易量，以供y轴label判断
		const { batch } = params;
		const start = Math.floor((batch[0].start / 100) * data.values.length);
		const end = Math.ceil((batch[0].end / 100) * data.values.length);
		maxData.value = Math.max(...data.values.slice(start, end).map(item => item[6]));
	});
};

watch(
	[() => props.data],
	() => {
		nextTick(() => {
			renderChart();
		});
	},
	{ immediate: true, deep: true }
);

defineExpose({
	refresh: renderChart
});
</script>

<style lang="less" scoped>
.mob-kchart-container {
	width: 100%;
	height: 400px;
}
</style>
